// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext } from 'react';

// MUI components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LaunchIcon from '@mui/icons-material/Launch';

// Section for import custom components --------------------------------------------------------------------------------
import { AppConfigContext } from '@providers/AppConfigProvider';

// Section for type definition -----------------------------------------------------------------------------------------
type MansionInquiryInContentsProps = {
  mansionName: string;
  estateHelpContactUrl: string;
};

// Section for render component ----------------------------------------------------------------------------------------
export default function MansionInquiryInContents(props: MansionInquiryInContentsProps) {
  const { assetUrlPrefix } = useContext(AppConfigContext);
  const { mansionName, estateHelpContactUrl } = props;

  // 赤丸付きテキストのスタイル
  const bulletTextClass =
    'relative pl-5 text-sm font-bold leading-[22px] before:absolute before:top-[7px] before:left-0 before:w-2 before:h-2 before:rounded-full before:bg-rf-red';

  return (
    <Box component="section" className="py-8 max-md:py-9 max-md:mx-5 max-md:mb-3">
      {/* PC: 2カラム横並び（幅が足りない場合は自動で縦並び） / SP: 縦並び */}
      <Box className="flex md:flex-row flex-col flex-wrap justify-center md:gap-16 gap-10 max-w-[1100px] mx-auto">
        {/* 左: 売り出し情報が知りたい */}
        <Box className="md:w-[518px] w-full flex flex-col items-center md:gap-10 gap-6">
          <Typography variant="h3" className="text-xl max-md:text-lg font-bold text-center leading-[28px]">
            {mansionName} の売出し情報が知りたい
          </Typography>
          <Box className="max-md:w-full flex flex-col items-center gap-6">
            <Box className="max-md:w-full flex md:flex-row flex-col items-center md:gap-6 gap-4">
              <picture>
                <source
                  srcSet={`${assetUrlPrefix}karte/mansions/detail/common/wantInfo_pc.webp`}
                  media="(min-width: 768px)"
                  type="image/webp"
                />
                <img
                  src={`${assetUrlPrefix}karte/mansions/detail/common/wantInfo_sp.webp`}
                  alt="売出し情報が知りたい"
                  className="md:w-[164px] md:h-[120px] w-[300px] max-md:h-[220px] object-cover"
                  loading="lazy"
                />
              </picture>
              <Box className="max-md:max-w-[300px] md:w-[272px] max-md:w-full mx-auto flex flex-col gap-3 max-md:gap-2">
                <Typography className={bulletTextClass}>このマンションが売り出されたら教えて</Typography>
                <Typography className={bulletTextClass}>理想のマンションを見逃したくない</Typography>
                <Typography className={bulletTextClass}>簡単！１分でお問い合わせ</Typography>
              </Box>
            </Box>
            <Link
              href={estateHelpContactUrl}
              target="_blank"
              rel="noopener"
              underline="none"
              className="group w-full md:w-auto"
            >
              <Box className="rf-btn-lg-spMax md:w-[448px] max-md:!w-full max-md:!max-w-[none] relative">
                この物件が出たら教えて
                <ArrowForwardRoundedIcon
                  sx={{ color: '#191919' }}
                  className="md:opacity-0 group-hover:opacity-100 absolute top-[18px] right-[12px] w-5 transition-opacity duration-150"
                />
              </Box>
            </Link>
          </Box>
        </Box>

        {/* 右: 査定してほしい */}
        <Box className="md:w-[518px] w-full flex flex-col items-center md:gap-10 gap-6">
          <Typography variant="h3" className="text-xl max-md:text-lg font-bold text-center leading-[28px]">
            {mansionName} を査定してほしい
          </Typography>
          <Box className="max-md:w-full flex flex-col items-center gap-6">
            <Box className="max-md:w-full flex md:flex-row flex-col items-center md:gap-9 gap-4">
              <img
                src={`${assetUrlPrefix}karte/mansions/detail/common/sales_affiliate.webp`}
                alt="査定してほしい"
                className="md:w-[164px] md:h-[120px] hidden md:block object-cover"
                loading="lazy"
              />
              <Box className="max-md:max-w-[300px] max-md:w-full mx-auto flex flex-col gap-3 max-md:gap-2 md:w-[248px]">
                <Typography className={bulletTextClass}>入力１分、最大６社の価格比較</Typography>
                <Typography className={bulletTextClass}>日本最多、利用者１,０００万人</Typography>
                <Typography className={bulletTextClass}>厳選１,５００社！悪徳業社は排除</Typography>
              </Box>
            </Box>
            <Box className="flex flex-col items-center gap-3 w-full md:w-auto">
              <Link
                href="https://t.felmat.net/fmcl?ak=J931Q.1.3191374.Y1330185"
                target="_blank"
                rel="nofollow noopener"
                underline="none"
                className="group w-full md:w-auto"
              >
                <Box className="rf-btn-lg-spMax md:w-[448px] relative">
                  売るといくらか見てみる
                  <LaunchIcon
                    sx={{ color: '#191919' }}
                    className="md:opacity-0 group-hover:opacity-100 absolute top-[18px] right-[12px] w-5 transition-opacity duration-150"
                  />
                </Box>
              </Link>
              <Typography className="text-2xs text-rf-black">
                ※ 国内最大級の不動産査定サービス「HOME4U」と提携しています。
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
